import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import pathVersionSurveyBase from './path-version-survey.json';
import { PathVersionSurveyDialogComponent } from './path-version-survey-dialog.component';

interface Version {
  version_id: number;
  path_id: number;
  organizations_id: number;
  name?: string;
  start_date: string;
  end_date: string;
  formatted_start_date: string;
  formatted_end_date: string;
  display_name: string;
  required_maintenance_minutes: number;
  recurrence_value: number | null;
  recurrence_unit: string | null;
  recurrence_type?: string;
  recurrence_calendar_anchor_date?: string | null;
  reset_on_fulfillment?: boolean;
  granted_version_ids?: number[];
  inherits_maintenance_from_version_id?: number | null;
  inherits_maintenance_from_path_name?: string | null;
  inherits_maintenance_from_version_name?: string | null;
  visibility?: string;
  version_end_date?: string | null;
  migrated_to_version_id?: number | null;
  auto_transfer_enabled: boolean;
  created_at: string;
  updated_at: string | null;
  requirements: any[];
  maintenance: any[];
}

@Component({
  selector: 'app-duna-educational-path-version-select',
  standalone: true,
  imports: [FormsModule, MatSelectModule, MatButtonModule],
  providers: [DatePipe],
  templateUrl: './duna-educational-path-version-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-educational-path-version-select.component.scss'],
})
export class DunaEducationalPathVersionSelectComponent
  implements OnInit, OnDestroy
{
  versions: Version[] = [];
  selectedVersion: Version | null = null;
  selectedVersionId: number | null = null;
  private subscription: Subscription | null = null;
  private storeSubscription: Subscription | null = null;
  private formDialogRef: MatDialogRef<PathVersionSurveyDialogComponent> | null =
    null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private componentDataSourceService: ComponentDataSourceService,
    private datePipe: DatePipe,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store
  ) {
    this.loadVersions();
  }

  compareVersions(v1: number, v2: number): boolean {
    return v1 === v2;
  }

  private loadVersions(): void {
    const dataSource = this.componentDataSourceService.getDataSource(
      'educational_path_versions'
    );
    if (dataSource) {
      dataSource.execute().subscribe({
        next: (versions: Version[]) => this.applyVersions(versions),
        error: (error) => {
          console.error('[Version] Error fetching versions:', error);
        },
      });
    }
  }

  private applyVersions(versions: Version[]): void {
    this.versions = versions;

    if (this.versions.length === 1 && !this.selectedVersionId) {
      const singleVersion = this.versions[0];
      this.autoSelectVersion(singleVersion.version_id);
    } else if (this.selectedVersionId !== null) {
      const matchingVersion = this.versions.find(
        (v) => v.version_id === this.selectedVersionId
      );
      this.selectedVersion = matchingVersion || null;
    }

    this.changeDetectorRef.detectChanges();
  }

  private autoSelectVersion(versionId: number): void {
    this.selectedVersionId = versionId;
    this.selectedVersion =
      this.versions.find((v) => v.version_id === versionId) || null;

    this.componentDataSourceService.setCurrentVersionId(versionId.toString());

    const currentUrl = window.location.pathname;
    const parts = currentUrl.split('/').filter(Boolean);
    const uddannelsesvejIndex = parts.indexOf('uddannelsesvej');
    if (uddannelsesvejIndex !== -1 && parts[uddannelsesvejIndex + 1]) {
      const uuid = parts[uddannelsesvejIndex + 1];
      this.router.navigate(['/uddannelsesvej', uuid, versionId]);
    }
  }

  private getPathUuidFromUrl(): string | null {
    const currentUrl = window.location.pathname;
    const parts = currentUrl.split('/').filter(Boolean);
    const uddannelsesvejIndex = parts.indexOf('uddannelsesvej');
    if (uddannelsesvejIndex !== -1 && parts[uddannelsesvejIndex + 1]) {
      return parts[uddannelsesvejIndex + 1];
    }
    return null;
  }

  ngOnInit() {
    this.storeSubscription = this.store
      .select((state: any) => state.data?.['educational_path_versions'])
      .pipe(
        filter((entry) => !!entry?.data && Array.isArray(entry.data)),
        distinctUntilChanged((a, b) => a?.lastUpdated === b?.lastUpdated),
        map((entry) => entry.data as Version[])
      )
      .subscribe((versions) => this.applyVersions(versions));

    this.subscription = this.route.url.subscribe((segments) => {
      const lastSegment = segments[segments.length - 1]?.path;
      const versionId =
        lastSegment && /^\d+$/.test(lastSegment)
          ? parseInt(lastSegment, 10)
          : null;
      this.selectedVersionId = versionId;

      if (this.selectedVersionId !== null) {
        this.componentDataSourceService.setCurrentVersionId(
          this.selectedVersionId.toString()
        );
      } else {
        this.componentDataSourceService.setCurrentVersionId(null);
      }

      if (this.versions.length > 0) {
        if (this.selectedVersionId !== null) {
          const matchingVersion = this.versions.find(
            (v) => v.version_id === this.selectedVersionId
          );
          this.selectedVersion = matchingVersion || null;
        } else if (this.versions.length === 1) {
          const singleVersion = this.versions[0];
          this.autoSelectVersion(singleVersion.version_id);
        }

        this.changeDetectorRef.detectChanges();
      }
    });
  }

  onVersionChange(versionId: number) {
    this.selectedVersionId = versionId;
    this.selectedVersion =
      this.versions.find((v) => v.version_id === versionId) || null;

    this.componentDataSourceService.setCurrentVersionId(versionId.toString());

    const uuid = this.getPathUuidFromUrl();
    if (uuid) {
      this.router.navigate(['/uddannelsesvej', uuid, versionId]);
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }
    if (this.formDialogRef) {
      this.formDialogRef.close();
    }
  }

  onAdd() {
    const uuid = this.getPathUuidFromUrl();
    if (!uuid) {
      return;
    }
    const surveyJson = {
      ...(pathVersionSurveyBase as object),
      data: {
        uuid,
        recurrence_type: false,
      },
    };
    this.openSurveyDialog('Ny version', surveyJson, 'add_path_version');
  }

  onEdit() {
    if (!this.selectedVersion) {
      return;
    }

    const v = this.selectedVersion;
    const surveyJson = {
      ...(pathVersionSurveyBase as object),
      data: {
        version_id: v.version_id,
        name: v.name ?? v.display_name,
        required_maintenance_minutes: v.required_maintenance_minutes,
        recurrence_value: v.recurrence_value ?? '',
        recurrence_unit: v.recurrence_unit ?? '',
        recurrence_type: v.recurrence_type === 'per_user',
        reset_on_fulfillment: v.reset_on_fulfillment ?? false,
        recurrence_calendar_anchor_date:
          v.recurrence_calendar_anchor_date ?? '',
        granted_version_ids: v.granted_version_ids ?? [],
        inherits_maintenance_from_version_id:
          v.inherits_maintenance_from_version_id ?? '',
        visibility: v.visibility ?? 'private',
        version_end_date: v.version_end_date ?? '',
        migrated_to_version_id: v.migrated_to_version_id ?? '',
      },
    };
    this.openSurveyDialog('Rediger version', surveyJson, 'update_path_version');
  }

  private openSurveyDialog(
    title: string,
    surveyJson: object,
    formReceive: string
  ): void {
    if (this.formDialogRef) {
      this.formDialogRef.close();
      this.formDialogRef = null;
    }
    this.formDialogRef = this.dialog.open(PathVersionSurveyDialogComponent, {
      width: 'min(560px, 96vw)',
      maxHeight: '90vh',
      data: { title, surveyJson, formReceive },
    });
    this.formDialogRef.afterClosed().subscribe(() => {
      this.formDialogRef = null;
    });
    this.changeDetectorRef.detectChanges();
  }
}
